import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { catalogSnapshotApi, type PublishStatusResponse } from './catalogSnapshotApi'
import { unpackCatalogSnapshot, type UnpackedCatalogSnapshot } from './catalogSnapshot'

export interface CatalogSnapshotState {
  isLoaded: boolean
  loading: boolean
  publishing: boolean
  version: number | null
  deployedAt: string | null
  error: string | null
  unpacked: UnpackedCatalogSnapshot | null
  publishStatus: PublishStatusResponse | null
}

const initialState: CatalogSnapshotState = {
  isLoaded: false,
  loading: false,
  publishing: false,
  version: null,
  deployedAt: null,
  error: null,
  unpacked: null,
  publishStatus: null,
}

export const fetchCatalogSnapshot = createAsyncThunk(
  'catalogSnapshot/fetch',
  async (force?: boolean) => {
    const raw = await catalogSnapshotApi.getSnapshot()
    const unpacked = unpackCatalogSnapshot(raw)
    return {
      version: unpacked.version,
      deployedAt: unpacked.deployedAt,
      unpacked,
    }
  },
  {
    condition: (force, { getState }) => {
      if (force) return true
      const state = (getState() as { catalogSnapshot?: CatalogSnapshotState }).catalogSnapshot
      return !state?.isLoaded && !state?.loading
    },
  },
)

export const publishCatalogSnapshot = createAsyncThunk(
  'catalogSnapshot/publish',
  async (_, { dispatch }) => {
    const res = await catalogSnapshotApi.publish()
    await dispatch(fetchCatalogSnapshot(true))
    return res
  },
)

export const fetchPublishStatus = createAsyncThunk(
  'catalogSnapshot/fetchStatus',
  async () => catalogSnapshotApi.getPublishStatus(),
)

export const catalogSnapshotSlice = createSlice({
  name: 'catalogSnapshot',
  initialState,
  reducers: {
    invalidateSnapshot(state) {
      state.isLoaded = false
      state.unpacked = null
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCatalogSnapshot.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchCatalogSnapshot.fulfilled, (state, action) => {
        state.loading = false
        state.isLoaded = true
        state.version = action.payload.version
        state.deployedAt = action.payload.deployedAt
        state.unpacked = action.payload.unpacked
      })
      .addCase(fetchCatalogSnapshot.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message ?? 'Không tải được bản chụp danh mục'
      })
      .addCase(publishCatalogSnapshot.pending, (state) => {
        state.publishing = true
      })
      .addCase(publishCatalogSnapshot.fulfilled, (state, action) => {
        state.publishing = false
        state.version = action.payload.version
      })
      .addCase(publishCatalogSnapshot.rejected, (state, action) => {
        state.publishing = false
        state.error = action.error.message ?? 'Xuất bản danh mục thất bại'
      })
      .addCase(fetchPublishStatus.fulfilled, (state, action) => {
        state.publishStatus = action.payload
        if (action.payload.version) {
          state.version = action.payload.version
          state.deployedAt = action.payload.deployed_at ?? null
        }
      })
  },
})

export const catalogSnapshotActions = catalogSnapshotSlice.actions
export const catalogSnapshotReducer = catalogSnapshotSlice.reducer
